import { Button, ConfigProvider, Flex, Modal } from "antd";
import { type ReactNode, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { type BlockerFunction, useBlocker } from "react-router";

import { theme as customTheme } from "../config/theme";
import ExclamationCircleFilled from "../icons/ExclamationCircleFilled.svg?react";

export interface UseNavigationWarningOptions {
  closable?: boolean;
  discardText?: ReactNode;
  icon?: ReactNode;
  modalText?: ReactNode;
  modalTitle?: ReactNode;
  saveText?: ReactNode;
  stayText?: ReactNode;
}

export interface UseNavigationWarningProps {
  isDirty: boolean;
  onLeaveWithoutSaving?: (proceed: () => void) => void | Promise<void>;
  onSaveAndLeave?: (proceed: () => void) => void | Promise<void>;
  onStay?: (reset: () => void) => void | Promise<void>;
  options?: UseNavigationWarningOptions;
}

export const useNavigationWarning = ({
  isDirty,
  onSaveAndLeave,
  onLeaveWithoutSaving,
  onStay,
  options = {},
}: UseNavigationWarningProps) => {
  const { t } = useTranslation(["common"]);
  const modalRef = useRef<ReturnType<typeof Modal.confirm> | null>(null);

  const {
    modalText = t("common:components.navigationWarning.description"),
    modalTitle = t("common:components.navigationWarning.title"),
    saveText = t("common:components.navigationWarning.confirm"),
    discardText = t("common:components.navigationWarning.discard"),
    stayText = t("common:components.navigationWarning.cancel"),
    closable = true,
    icon = <ExclamationCircleFilled />,
  } = options;

  const shouldBlock = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) =>
      isDirty && currentLocation.pathname !== nextLocation.pathname,
    [isDirty],
  );

  const blocker = useBlocker(shouldBlock);

  const proceed = useCallback(() => {
    modalRef.current?.destroy();
    blocker.proceed?.();
  }, [blocker]);

  const reset = useCallback(() => {
    modalRef.current?.destroy();
    blocker.reset?.();
  }, [blocker]);

  const handleSaveAndLeave = useCallback(async () => {
    if (onSaveAndLeave) {
      await onSaveAndLeave(proceed);
    } else {
      proceed();
    }
  }, [onSaveAndLeave, proceed]);

  const handleLeaveWithoutSaving = useCallback(async () => {
    if (onLeaveWithoutSaving) {
      await onLeaveWithoutSaving(proceed);
    } else {
      proceed();
    }
  }, [onLeaveWithoutSaving, proceed]);

  const handleStay = useCallback(async () => {
    if (onStay) {
      await onStay(reset);
    } else {
      reset();
    }
  }, [onStay, reset]);

  useEffect(() => {
    if (blocker.state === "blocked") {
      ConfigProvider.config({
        theme: customTheme,
      });

      modalRef.current = Modal.confirm({
        title: (
          <Flex align="center">
            <span style={{ marginLeft: "8px" }}>{modalTitle}</span>
          </Flex>
        ),
        content: modalText,
        closable,
        maskClosable: closable,
        icon: <span style={{ fontSize: "17px" }}>{icon}</span>,
        onCancel: handleStay,
        footer: (
          <Flex justify="flex-end" gap="small" style={{ marginTop: 24 }}>
            {onStay && <Button onClick={handleStay}>{stayText}</Button>}

            {onLeaveWithoutSaving && (
              <Button danger onClick={handleLeaveWithoutSaving}>
                {discardText}
              </Button>
            )}

            <Button type="primary" onClick={handleSaveAndLeave}>
              {saveText}
            </Button>
          </Flex>
        ),
      });
    }

    return () => {
      modalRef.current?.destroy();
    };
  }, [
    blocker.state,
    handleSaveAndLeave,
    handleLeaveWithoutSaving,
    handleStay,
    onStay,
    onLeaveWithoutSaving,
    modalText,
    modalTitle,
    saveText,
    discardText,
    stayText,
    closable,
    icon,
  ]);

  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty && blocker.state !== "unblocked") {
        e.preventDefault();
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [isDirty, blocker.state]);
};
